export type CardDensity = `compact` | `comfortable`
export type CardSlotName = `subtitle` | `hero` | `meta`

export interface ListCardConfig {
  version: 1
  density: CardDensity
  showAvatar: boolean
  showAge: boolean
  showCommunications: boolean
  slots: {
    subtitle: string | null
    hero: string | null
    /** Combined "Additional details" — up to META_MAX fields, rendered in the content zone. */
    meta: string[]
  }
}

export const META_MAX = 3

/** The stored key predates lists. It stays, so nobody's saved card resets. */
export const LIST_CARD_CONFIG_SETTING = `pipeline-card-config`

export const DEFAULT_LIST_CARD_CONFIG: ListCardConfig = {
  version: 1,
  density: `comfortable`,
  showAvatar: true,
  showAge: true,
  showCommunications: true,
  slots: {
    subtitle: `location`,
    hero: `scheduledProduction`,
    meta: [`tasks`],
  },
}

type EligibleFn = (fieldId: string, slot: CardSlotName) => boolean

function pickSingle(id: string | null | undefined, slot: CardSlotName, isEligible: EligibleFn): string | null {
  return id && isEligible(id, slot) ? id : null
}

export function getListCardConfig(raw: unknown, isEligible: EligibleFn): ListCardConfig {
  const d = DEFAULT_LIST_CARD_CONFIG
  if (!raw || typeof raw !== `object`) return d
  const r = raw as Partial<ListCardConfig>
  const slots = (r.slots ?? d.slots) as {
    subtitle?: string | null
    hero?: string | null
    // `signal` is retired; read it only to migrate legacy saved configs into `meta`.
    signal?: string | null
    meta?: string[]
  }
  const legacySignal = typeof slots.signal === `string` ? [slots.signal] : []
  const rawMeta = Array.isArray(slots.meta) ? slots.meta : d.slots.meta
  const meta = [...new Set([...legacySignal, ...rawMeta])].filter((id) => isEligible(id, `meta`)).slice(0, META_MAX)
  return {
    version: 1,
    density: r.density === `compact` || r.density === `comfortable` ? r.density : d.density,
    showAvatar: typeof r.showAvatar === `boolean` ? r.showAvatar : d.showAvatar,
    showAge: typeof r.showAge === `boolean` ? r.showAge : d.showAge,
    showCommunications: typeof r.showCommunications === `boolean` ? r.showCommunications : d.showCommunications,
    slots: {
      subtitle: pickSingle(slots.subtitle, `subtitle`, isEligible),
      hero: pickSingle(slots.hero, `hero`, isEligible),
      meta,
    },
  }
}
